import type { FC } from "react";
import { useMemo } from "react";

import type { AddressFormProviderProps } from "@ui/components/AddressFormProvider";
import { CountryField } from "@ui/components/AddressFormProvider";
import { Controller, useFormContext } from "@ui/components/Form";
import { Select, TextInput } from "@ui/components/Inputs";
import { PostCode } from "@ui/components/PostCode";
import { useTranslations } from "@ui/providers/useTranslations";

import type { KsaProvince } from "@jti/fe-common";
import type { PostCodeConfig } from "@jti/magento";

import styles from "./SaudiArabiaForm.module.scss";

type SaudiArabiaFormProps = Pick<AddressFormProviderProps, "fieldName"> & {
  ksaProvinces?: KsaProvince[];
  postCodeConfig?: PostCodeConfig;
};

const fieldNameDefault = (name: string) => name;

export const SaudiArabiaForm: FC<SaudiArabiaFormProps> = ({
  fieldName = fieldNameDefault,
  ksaProvinces = [],
  postCodeConfig,
}) => {
  const { setValue, trigger, control, watch } = useFormContext();
  const { __ } = useTranslations();

  const selectedProvince = watch(fieldName("provinceName"));

  const provinceOptions = useMemo(() => ksaProvinces.map((p) => ({ label: p.name, value: p.name })), [ksaProvinces]);

  const cityOptions = useMemo(() => {
    const province = ksaProvinces.find((p) => p.name === selectedProvince);
    return (province?.cities ?? []).map((c) => ({ label: c, value: c }));
  }, [ksaProvinces, selectedProvince]);

  return (
    <div className={styles.wrapper}>
      <Controller
        control={control}
        name={fieldName("shortAddress")}
        rules={{
          required: __("form_validation.field_required"),
          pattern: {
            value: /^[A-Za-z]{4}\d{4}$/,
            message: __("address_fields.short_address_invalid"),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            {...field}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value.toUpperCase())}
            isError={!!error}
            errorText={error?.message}
            required
            data-testid="shortAddressKSA"
            label={__("address_fields.short_address")}
            placeholder={__("address_fields.short_address_placeholder")}
            autoComplete="off"
          />
        )}
      />
      <PostCode fieldName={fieldName} config={postCodeConfig} />
      <div className={styles.row}>
        <Controller
          control={control}
          name={fieldName("provinceName")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              options={provinceOptions}
              isSearchable
              onChange={(option: { label: string; value: string } | null) => {
                field.onChange(option?.value ?? "");
                setValue(fieldName("city"), "");
                trigger(fieldName("city"));
              }}
              value={field.value ? { label: field.value, value: field.value } : null}
              isError={!!error}
              errorText={error?.message}
              isHelperTextVisible={!!error}
              showRequiredAsterisk
              required
              dataTestId="provinceKSA"
              label={__("address_fields.province")}
              placeholder={__("address_fields.select_an_option")}
            />
          )}
        />
        <Controller
          control={control}
          name={fieldName("city")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => (
            <Select
              {...field}
              options={cityOptions}
              isSearchable
              isDisabled={!selectedProvince}
              onChange={(option: { label: string; value: string } | null) => field.onChange(option?.value ?? "")}
              value={field.value ? { label: field.value, value: field.value } : null}
              isError={!!error}
              errorText={error?.message}
              helperText={!selectedProvince && !error ? __("address_fields.select_province_to_enable_city") : undefined}
              isHelperTextVisible={(!selectedProvince && !error) || !!error}
              showRequiredAsterisk
              required
              dataTestId="cityKSA"
              label={__("address_fields.city")}
              placeholder={__("address_fields.select_an_option")}
            />
          )}
        />
      </div>
      <div className={styles.row}>
        <Controller
          control={control}
          name={fieldName("district")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              isError={!!error}
              errorText={error?.message}
              required
              data-testid="districtKSA"
              label={__("address_fields.district")}
              placeholder={__("address_fields.district_placeholder")}
              autoComplete="address-level3"
            />
          )}
        />
        <Controller
          control={control}
          name={fieldName("streetName")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              isError={!!error}
              errorText={error?.message}
              required
              data-testid="streetNameKSA"
              label={__("address_fields.street_name")}
              placeholder={__("address_fields.street_name_placeholder")}
              autoComplete="address-line1"
            />
          )}
        />
      </div>
      <div className={styles.fullRow}>
        <Controller
          control={control}
          name={fieldName("streetAdditional")}
          rules={{ required: __("form_validation.field_required") }}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              isError={!!error}
              errorText={error?.message}
              required
              data-testid="streetAdditionalKSA"
              label={__("address_fields.street_additional")}
              placeholder={__("address_fields.street_additional_placeholder")}
              autoComplete="address-line3"
            />
          )}
        />
      </div>
      <div className={styles.fullRow}>
        <CountryField fieldName={fieldName} />
      </div>
    </div>
  );
};
